import { createContext, ReactNode, useContext, useState } from 'react'

import { Domain } from '@f-fertighaus/applications/Next/pages/house-detail-page/HDPContent'
import { HouseDetail } from '@f-fertighaus/applications/Next/types/houseDetailsResponse'
import { VendorDetails } from '@f-fertighaus/applications/Next/types/vendorResponse'
import {
  FavoriteHouse,
  useFavorite
} from '@f-fertighaus/applications/Next/utils/context/useFavorite'
import { useHouseRegionalPrices } from '@f-fertighaus/applications/Next/utils/context/useHouseRegionalPrices'

const HouseDetailContext = createContext<
  | {
      state: HouseDetail
      vendor: VendorDetails
      favoriteHouse: FavoriteHouse
      domain: Domain
      reviewsActive: boolean
      featureFlags: string[]
    }
  | undefined
>(undefined)

export function useHouseDetail() {
  const context = useContext(HouseDetailContext)

  if (!context) {
    throw new Error('useHouseDetail must be used within HouseDetailProvider')
  }

  return context
}

export function HouseDetailProvider(props: {
  data: HouseDetail
  vendor: VendorDetails
  children: ReactNode
  domain: Domain
  reviewsActive: boolean
  featureFlags: string[]
}) {
  const [state] = useState(props.data)
  const favoriteHouse = useFavorite(props.data.nid)
  const regionalPrices = useHouseRegionalPrices({
    houseNid: props.data.nid,
    vendorHasRegionalPrices: props.data.vendor_verbose.has_regional_prices
  })

  const stateWithOverwrittenPrices = regionalPrices
    ? { ...state, ...regionalPrices }
    : state

  const value = {
    state: stateWithOverwrittenPrices,
    vendor: props.vendor,
    favoriteHouse,
    domain: props.domain,
    reviewsActive: props.reviewsActive,
    featureFlags: props.featureFlags
  }

  return (
    <HouseDetailContext.Provider value={value}>
      {props.children}
    </HouseDetailContext.Provider>
  )
}
